/* global Office, Excel */

import { apiService } from '../services/apiService.js';
import { excelService } from '../services/excelService.js';
import { authService } from '../services/authService.js';
import { CONFIG } from '../config/constants.js';

Office.onReady(() => {
  // Register all ribbon ExecuteFunction handlers
  Office.actions.associate('syncRawData', syncRawData);
});

// ── Helpers ──────────────────────────────────────────────────────────────────

function showStatus(msg) {
  try {
    Office.context.ui.displayDialogAsync(
      `data:text/html,<html><body style="font-family:sans-serif;padding:16px">${encodeURIComponent(msg)}</body></html>`,
      { height: 20, width: 40 },
      () => {}
    );
  } catch { /* non-critical */ }
  console.log('[SyncRawData]', msg);
}

async function ensureToken() {
  const token = authService.getAccessToken();
  if (!token) throw new Error('Please sign in via the Corvenia task pane first.');
  return token;
}

// Convert column letter to index (A=1)
function colIndex(col) {
  return col.toUpperCase().charCodeAt(0) - 64;
}

/**
 * Convert API flat rows to 2-D array for replaceTableData.
 * Keeps only keys listed in `columns` in that order.
 */
function toRows(records, columns) {
  return records.map(r => columns.map(c => r[c] ?? null));
}

// ── Main sync function (Ribbon → ExecuteFunction) ─────────────────────────

/**
 * Ribbon "Sync Data" button handler.
 * 1. Reads cfg_* named ranges from _Config sheet.
 * 2. Fetches raw data from all relevant REST endpoints.
 * 3. Writes to Raw_* named Excel tables (non-destructive — only replaces body rows).
 * 4. Updates cfg_LastSyncTime.
 *
 * @param {Office.AddinCommands.Event} event
 */
async function syncRawData(event) {
  try {
    await ensureToken();
    console.log('[SyncRawData] Starting raw data sync...');

    // 1. Read configuration from _Config sheet
    let cfg;
    try {
      cfg = await excelService.readConfigValues();
    } catch (err) {
      // _Config sheet not yet created; bootstrap it then retry
      console.log('[SyncRawData] _Config not found, bootstrapping...');
      await bootstrapWorkbook();
      cfg = await excelService.readConfigValues();
    }

    console.log('[SyncRawData] Config:', JSON.stringify(cfg));

    const scope = (cfg.syncScope || 'All').toLowerCase();
    const syncAll = scope === 'all';

    // If no party IDs are configured, pass null → API returns all accessible parties
    // (no blocking — always proceed with the sync)

    // 3. Sync in parallel where possible
    const syncPromises = [];

    if (syncAll || scope === 'parties') {
      syncPromises.push(syncPartiesAndCurrencies());
    }

    if (syncAll || scope === 'trialbalance') {
      syncPromises.push(syncTrialBalance(cfg));
    }

    if (syncAll || scope === 'eliminations') {
      syncPromises.push(syncEliminations(cfg));
    }

    if (syncAll || scope === 'ownership') {
      syncPromises.push(syncOwnership());
    }

    if (syncAll || scope === 'masterchart') {
      syncPromises.push(syncMasterChart());
    }

    if (syncAll || scope === 'currencies') {
      syncPromises.push(syncCurrenciesAndRates());
    }

    await Promise.allSettled(syncPromises);
    await excelService.updateLastSyncTime();

    console.log('[SyncRawData] Sync complete.');
  } catch (err) {
    console.error('[SyncRawData] Error:', err);
    // Surface the error in the status cell
    try {
      await Excel.run(async (context) => {
        const namedItem = context.workbook.names.getItemOrNullObject(CONFIG.NAMED_RANGES.LAST_SYNC_TIME);
        namedItem.load('name');
        await context.sync();
        if (!namedItem.isNullObject) {
          namedItem.getRange().values = [[`ERROR: ${err.message}`]];
          await context.sync();
        }
      });
    } catch { /* ignore */ }
  } finally {
    event.completed();
  }
}

// ── Individual sync functions ─────────────────────────────────────────────

async function syncPartiesAndCurrencies() {
  try {
    const parties = await apiService.fetchPartiesDetail();
    const partyRows = toRows(parties, [
      'partyId', 'name', 'orgNumber', 'countryCode', 'companyType', 'enableSync', 'startDate', 'endDate'
    ]);
    await excelService.replaceTableData(
      CONFIG.SHEETS.RAW_PARTIES,
      'tbl_RawParties',
      ['PartyId', 'Name', 'OrgNumber', 'CountryCode', 'CompanyType', 'EnableSync', 'StartDate', 'EndDate'],
      partyRows
    );
    console.log('[SyncRawData] Raw_Parties written:', parties.length, 'rows');
  } catch (err) {
    console.error('[SyncRawData] syncPartiesAndCurrencies failed:', err);
  }
}

async function syncCurrenciesAndRates() {
  try {
    // Currencies master
    const currencies = await apiService.fetchCurrencies();
    await excelService.replaceTableData(
      CONFIG.SHEETS.RAW_CURRENCIES,
      'tbl_RawCurrencies',
      ['CurrencyId', 'Code', 'DisplayName', 'Symbol', 'NumericCode', 'Countries'],
      toRows(currencies, ['id', 'code', 'displayName', 'symbol', 'numericCode', 'countries'])
    );
    console.log('[SyncRawData] Raw_Currencies written:', currencies.length, 'rows');

    // Exchange rates (all, no filter — large dataset, user can filter in Excel)
    const ratesResp = await apiService.fetchExchangeRates({ limit: 50000 });
    await excelService.replaceTableData(
      CONFIG.SHEETS.RAW_EXCHANGE_RATES,
      'tbl_RawExchangeRates',
      ['FromCurrency', 'ToCurrency', 'Rate', 'EffectiveDate'],
      toRows(ratesResp, ['fromCurrencyCode', 'toCurrencyCode', 'rate', 'effectiveDate'])
    );
    console.log('[SyncRawData] Raw_ExchangeRates written:', ratesResp.length, 'rows');
  } catch (err) {
    console.error('[SyncRawData] syncCurrenciesAndRates failed:', err);
  }
}

async function syncTrialBalance(cfg) {
  try {
    const resp = await apiService.fetchTrialBalanceFlat({
      partyIds: cfg.partyIds?.length > 0 ? cfg.partyIds : null,
      groupEntityId: cfg.groupEntityId ?? null,
      year: cfg.year,
      periodType: cfg.periodType,
      period: cfg.period,
      profileId: cfg.profileId,
      includeCompanyElimination: cfg.includeCompanyElimination ?? false,
      includeHistoricalData: cfg.includeHistoricalData,
      partyAccountGrain: !(cfg.includeCompanyElimination ?? false)
    });

    const rawRows = toRows(resp.rows, [
      'partyId', 'partyName', 'partyAccountNumber', 'partyAccountName',
      'year', 'month', 'monthName',
      'masterAccountNumber', 'masterAccountName', 'accountClass',
      'currencyCode', 'balanceChange', 'balanceIn', 'balanceOut', 'eliminationsApplied'
    ]);
    let partyNameById = {};
    try {
      partyNameById = await excelService.readPartyNameLookupFromRawTable();
    } catch { /* ignore */ }
    const rows = rawRows.map((r) => {
      const out = [...r];
      if (!String(out[1] ?? '').trim()) {
        const name = partyNameById[Number(out[0])];
        if (name) out[1] = name;
      }
      return [...out.slice(0, -1), false];
    });

    await excelService.replaceTableData(
      CONFIG.SHEETS.RAW_TRIAL_BALANCE,
      'tbl_RawTrialBalance',
      ['PartyId', 'PartyName', 'PartyAccountNumber', 'PartyAccountName', 'Year', 'Month', 'MonthName',
       'AccountNumber', 'AccountName', 'AccountClass',
       'Currency', 'BalanceChange', 'BalanceIn', 'BalanceOut', 'EliminationsApplied'],
      rows
    );
    console.log('[SyncRawData] Raw_TrialBalance written:', rows.length, 'rows');
  } catch (err) {
    console.error('[SyncRawData] syncTrialBalance failed:', err);
  }
}

async function syncEliminations(cfg) {
  try {
    const rows = await apiService.fetchIntercompanyEliminations(
      cfg.partyIds?.length > 0 ? cfg.partyIds : [],
      cfg.year, 1,
      cfg.year, 12
    );

    await excelService.replaceTableData(
      CONFIG.SHEETS.RAW_ELIMINATIONS,
      'tbl_RawEliminations',
      ['EliminationId', 'PartyId', 'PartyName', 'ReciprocalPartyId', 'ReciprocalPartyName',
       'Year', 'Month', 'AccountNumber', 'Amount', 'CurrencyId', 'Status', 'Description'],
      toRows(rows, [
        'eliminationId', 'partyId', 'partyName', 'reciprocalPartyId', 'reciprocalPartyName',
        'year', 'month', 'accountNumber', 'amount', 'currencyId', 'status', 'description'
      ])
    );
    console.log('[SyncRawData] Raw_Eliminations written:', rows.length, 'rows');
  } catch (err) {
    console.error('[SyncRawData] syncEliminations failed:', err);
  }
}

async function syncOwnership() {
  try {
    const ownerships = await apiService.fetchPartyOwnerships();
    await excelService.replaceTableData(
      CONFIG.SHEETS.RAW_OWNERSHIP,
      'tbl_RawOwnership',
      ['OwnedPartyId', 'OwnedPartyName', 'OwnerPartyId', 'OwnerPartyName',
       'OwnershipPct', 'OwnershipType', 'EffectiveFrom', 'EffectiveTo', 'Notes'],
      toRows(ownerships, [
        'ownedPartyId', 'ownedPartyName', 'ownerPartyId', 'ownerPartyName',
        'ownershipPercentage', 'ownershipType', 'effectiveFrom', 'effectiveTo', 'notes'
      ])
    );
    console.log('[SyncRawData] Raw_Ownership written:', ownerships.length, 'rows');
  } catch (err) {
    console.error('[SyncRawData] syncOwnership failed:', err);
  }
}

async function syncMasterChart() {
  try {
    const [profiles, accounts, mappings] = await Promise.all([
      apiService.fetchMasterProfiles(),
      apiService.fetchMasterAccounts(),
      apiService.fetchAccountMappings()
    ]);

    await excelService.replaceTableData(
      CONFIG.SHEETS.RAW_MASTER_PROFILES,
      'tbl_RawMasterProfiles',
      ['ProfileId', 'Name'],
      toRows(profiles, ['profileId', 'name'])
    );

    await excelService.replaceTableData(
      CONFIG.SHEETS.RAW_MASTER_ACCOUNTS,
      'tbl_RawMasterAccounts',
      ['ProfileId', 'ProfileName', 'AccountId', 'AccountNumber', 'AccountName',
       'ParentAccountId', 'Statement', 'Section', 'NormalBalance', 'CashFlowClass', 'IsCash', 'IsContra'],
      toRows(accounts, [
        'profileId', 'profileName', 'accountId', 'accountNumber', 'accountName',
        'parentAccountId', 'statement', 'section', 'normalBalance', 'cashFlowClass', 'isCash', 'isContra'
      ])
    );

    await excelService.replaceTableData(
      CONFIG.SHEETS.RAW_ACCOUNT_MAPPINGS,
      'tbl_RawAccountMappings',
      ['PartyId', 'ProfileId', 'PartyAccountNumber', 'PartyAccountName', 'MasterAccountId', 'MasterAccountNumber', 'MasterAccountName'],
      toRows(mappings, [
        'partyId', 'profileId', 'partyAccountNumber', 'partyAccountName', 'masterAccountId', 'masterAccountNumber', 'masterAccountName'
      ])
    );

    console.log('[SyncRawData] Master chart written — profiles:', profiles.length,
      'accounts:', accounts.length, 'mappings:', mappings.length);
  } catch (err) {
    console.error('[SyncRawData] syncMasterChart failed:', err);
  }
}

/**
 * First-run bootstrapper: fetches parties + currencies, writes Raw_* tables,
 * then builds the _Config sheet with pre-populated lookups.
 */
async function bootstrapWorkbook() {
  console.log('[Bootstrap] First-run setup...');

  let parties = [];
  let currencies = [];
  let groupEntities = [];

  try {
    [parties, currencies] = await Promise.all([
      apiService.fetchPartiesDetail(),
      apiService.fetchCurrencies()
    ]);
  } catch (err) {
    console.error('[Bootstrap] Failed to fetch initial data:', err);
  }

  try {
    groupEntities = await apiService.fetchGroupEntities();
  } catch {
    groupEntities = [];
  }

  // Write Raw_Parties
  if (parties.length > 0) {
    await excelService.replaceTableData(
      CONFIG.SHEETS.RAW_PARTIES, 'tbl_RawParties',
      ['PartyId', 'Name', 'OrgNumber', 'CountryCode', 'CompanyType', 'EnableSync', 'StartDate', 'EndDate'],
      toRows(parties, ['partyId', 'name', 'orgNumber', 'countryCode', 'companyType', 'enableSync', 'startDate', 'endDate'])
    );
  }

  // Write Raw_Currencies
  if (currencies.length > 0) {
    await excelService.replaceTableData(
      CONFIG.SHEETS.RAW_CURRENCIES, 'tbl_RawCurrencies',
      ['CurrencyId', 'Code', 'DisplayName', 'Symbol', 'NumericCode', 'Countries'],
      toRows(currencies, ['id', 'code', 'displayName', 'symbol', 'numericCode', 'countries'])
    );
  }

  const currencyCodes = (currencies || []).map((c) => c.code).filter(Boolean);
  const partyNames = parties.map(p => ({ partyId: p.partyId, name: p.name }));
  await excelService.buildControlSheet(new Date().getFullYear(), partyNames, currencyCodes, [], {
    groupEntities,
    profiles: [],
  });

  console.log('[Bootstrap] Complete — edit _Config and click Sync Data to load financial data.');
}
